import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { onSessionExpired, renewSession, setAccessToken } from '../api/client.ts'
import { COMPANY_INACTIVE_SESSION_MESSAGE } from '../api/messages.ts'
import { auth } from '../api/resources.ts'
import type { AthenaRole, AthenaScope, CurrentUserResponse } from '../api/types.ts'

export type AuthStatus = 'checking' | 'anonymous' | 'authenticated'

/**
 * Cadência da renovação de fundo. O access token vale 15 min; 10 dá folga para
 * uma renovação falhar e a seguinte ainda acontecer dentro da validade.
 */
const RENEW_EVERY_MS = 10 * 60 * 1000

interface AuthValue {
  status: AuthStatus
  user: CurrentUserResponse | null
  /**
   * O que escolhe a experiência: `platform` (Black Ghost) ou `tenant` (empresa
   * cliente). Vem de `GET /auth/me` — nunca do JWT, nunca inferido dos papéis.
   */
  scope: AthenaScope | null
  /** `null` na Black Ghost. */
  companyId: string | null
  /** `null` na Black Ghost. */
  companyName: string | null
  isPlatform: boolean
  isTenant: boolean
  /** `platform_admin` satisfaz tudo que `platform_staff` satisfaz. */
  isPlatformAdmin: boolean
  /** Gerencia os usuários da própria empresa. */
  isTenantAdmin: boolean
  /** Alias de `isPlatformAdmin`, usado pelas telas e ações exclusivas da Black Ghost. */
  isAdmin: boolean
  /**
   * Por que a última sessão acabou, quando o motivo merece ser dito na tela de
   * login (a empresa da conta foi desativada). `null` no caso comum.
   */
  endedReason: string | null
  signIn: (username: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  signOutEverywhere: () => Promise<void>
  /**
   * Volta ao login sem falar com o servidor. É o caminho depois de trocar a
   * própria senha: o servidor já derrubou todas as sessões, inclusive esta, e
   * um `/logout` aqui só tomaria 401.
   */
  endSession: () => void
}

const AuthContext = createContext<AuthValue | null>(null)

function hasRole(user: CurrentUserResponse | null, role: AthenaRole): boolean {
  return user?.roles.includes(role) ?? false
}

/**
 * Sessão da aplicação.
 *
 * Na carga não há token em memória — mas pode haver o cookie `HttpOnly` de uma
 * sessão anterior. Por isso o primeiro ato é `GET /auth/me`: se o cookie ainda
 * valer, o cliente renova sozinho e o operador não vê tela de login; se não
 * valer, cai para anônimo. É a única forma de saber, já que o cookie não é
 * legível pelo JavaScript.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('checking')
  const [user, setUser] = useState<CurrentUserResponse | null>(null)
  const [endedReason, setEndedReason] = useState<string | null>(null)

  const clear = useCallback(() => {
    setAccessToken(null)
    setUser(null)
    setStatus('anonymous')
  }, [])

  // Qualquer chamada que esgote a renovação avisa por aqui, de onde quer que
  // ela tenha partido — inclusive uma conta desativada no meio da sessão. O
  // corte da empresa (`401 company_inactive` no refresh, §1.5) ganha frase
  // própria: "sessão expirada" mandaria o usuário tentar entrar de novo à toa.
  useEffect(
    () =>
      onSessionExpired((code) => {
        if (code === 'company_inactive') setEndedReason(COMPANY_INACTIVE_SESSION_MESSAGE)
        clear()
      }),
    [clear],
  )

  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false

    auth
      .me(controller.signal)
      .then((current) => {
        if (cancelled) return
        setUser(current)
        setStatus('authenticated')
      })
      .catch(() => {
        if (cancelled || controller.signal.aborted) return
        clear()
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [clear])

  /**
   * Renovação periódica enquanto a aplicação está aberta.
   *
   * As telas de gestão não precisam disto — elas renovam ao tomar 401. Quem
   * precisa é o stream do console: o `EventSource` não aceita header e autentica
   * pelo cookie `athena.jwt`, de 15 minutos. Sem esta renovação, a primeira
   * queda de rede depois de 15 minutos encerraria o stream em definitivo, e o
   * operador que deixou o painel num monitor secundário veria "desconectado"
   * sem nenhuma queda real ter acontecido.
   *
   * Uma renovação por vez, sempre: `renewSession` compartilha a promessa com a
   * renovação disparada por 401, e o refresh token rotaciona a cada uso.
   */
  useEffect(() => {
    if (status !== 'authenticated') return

    const timer = setInterval(() => {
      // A falha já avisa por `onSessionExpired`; aqui não há nada a fazer além
      // de não deixar a rejeição escapar como erro não tratado.
      void renewSession().catch(() => undefined)
    }, RENEW_EVERY_MS)

    return () => clearInterval(timer)
  }, [status])

  const signIn = useCallback(async (username: string, password: string) => {
    const session = await auth.login({ username, password })

    // Só o access token entra em memória. O refresh fica no cookie HttpOnly,
    // que é onde ele não pode ser lido por script injetado.
    setAccessToken(session.accessToken)
    setEndedReason(null)

    // O login já traz `scope` e `companyId`, mas não o nome da empresa; `/me` é
    // a fonte de verdade da experiência (§2.5). Se ele falhar, o corpo do login
    // basta para escolher a experiência — só o rótulo da empresa fica vazio.
    const current = await auth.me().catch(() => null)
    setUser(
      current ?? {
        id: session.user.id,
        username: session.user.username,
        email: session.user.email,
        displayName: session.user.displayName,
        roles: session.user.roles,
        lastLoginAt: session.user.lastLoginAt,
        scope: session.user.scope,
        companyId: session.user.companyId,
        companyName: null,
        mustChangePassword: session.user.mustChangePassword,
      },
    )
    setStatus('authenticated')
  }, [])

  const signOut = useCallback(async () => {
    // Idempotente no servidor: sair sem sessão é 204. Falhar aqui não pode
    // prender o operador numa tela que ele mandou fechar.
    await auth.logout().catch(() => undefined)
    clear()
  }, [clear])

  const signOutEverywhere = useCallback(async () => {
    await auth.logoutAll().catch(() => undefined)
    clear()
  }, [clear])

  const value = useMemo<AuthValue>(() => {
    const scope = user?.scope ?? null
    const isPlatform = scope === 'platform'
    const isTenant = scope === 'tenant'
    // O papel só conta dentro do escopo certo: o backend já recusa misturar,
    // e a interface não vai ser a exceção.
    const isPlatformAdmin = isPlatform && hasRole(user, 'platform_admin')
    return {
      status,
      user,
      scope,
      companyId: isTenant ? (user?.companyId ?? null) : null,
      companyName: isTenant ? (user?.companyName ?? null) : null,
      isPlatform,
      isTenant,
      isPlatformAdmin,
      isTenantAdmin: isTenant && hasRole(user, 'tenant_admin'),
      isAdmin: isPlatformAdmin,
      endedReason,
      signIn,
      signOut,
      signOutEverywhere,
      endSession: clear,
    }
  }, [status, user, endedReason, signIn, signOut, signOutEverywhere, clear])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext)
  if (value === null) throw new Error('useAuth precisa estar dentro de <AuthProvider>.')
  return value
}
